import type { DashboardPrefsMap } from './dashboardPrefsStorage';
import { applyAccentPaletteToRoot } from './accentPalette';

const ACCENT_HEX: Record<string, string> = {
  red: '#c41e1e',
  rose: '#f43f5e',
  amber: '#f59e0b',
  emerald: '#10b981',
};

/** BCP 47 tag for Intl (currency, dates) from appearance prefs. */
export function localeTagFromPrefs(prefs: DashboardPrefsMap): string {
  const lang = prefs.appearance_language === 'es' ? 'es' : 'en';
  if (prefs.appearance_region === 'intl') {
    return lang === 'es' ? 'es' : 'en-GB';
  }
  return lang === 'es' ? 'es-US' : 'en-US';
}

function resolveTheme(_prefs: DashboardPrefsMap): 'dark' {
  return 'dark';
}

export function applyDashboardPrefsToDocument(prefs: DashboardPrefsMap): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const theme = resolveTheme(prefs);
  root.dataset.theme = theme;
  root.dataset.accent = typeof prefs.appearance_accent === 'string' ? prefs.appearance_accent : 'red';
  root.dataset.fontSize = typeof prefs.appearance_font_size === 'string' ? prefs.appearance_font_size : 'sm';
  root.dataset.density = typeof prefs.appearance_density === 'string' ? prefs.appearance_density : 'compact';
  root.dataset.productView = typeof prefs.appearance_product_view === 'string' ? prefs.appearance_product_view : 'grid';
  root.dataset.reduceMotion = prefs.appearance_reduce_motion === true ? '1' : '0';
  root.dataset.highContrast = prefs.appearance_high_contrast === true ? '1' : '0';
  root.dataset.regionDisplay = typeof prefs.appearance_region === 'string' ? prefs.appearance_region : 'us';
  root.dataset.currencyDisplay = typeof prefs.appearance_currency === 'string' ? prefs.appearance_currency : 'USD';

  const lang = prefs.appearance_language === 'es' ? 'es' : 'en';
  root.lang = lang;

  const accent = ACCENT_HEX[root.dataset.accent || 'red'] || ACCENT_HEX.red;
  root.style.setProperty('--district-accent', accent);
  root.style.setProperty('--district-accent-muted', `${accent}99`);
  applyAccentPaletteToRoot(root, accent);

  syncThemeColorMeta(theme);
}

function syncThemeColorMeta(_theme: 'dark'): void {
  try {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    meta.setAttribute('content', '#0a0a0a');
  } catch {
    /* */
  }
}

export function formatMoneyWithPrefs(amount: number, prefs?: DashboardPrefsMap | null): string {
  const p = prefs || ({} as DashboardPrefsMap);
  const cur = p.appearance_currency === 'EUR' ? 'EUR' : 'USD';
  const locale = localeTagFromPrefs(p);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return cur === 'EUR' ? `€${Math.round(amount)}` : `$${Math.round(amount)}`;
  }
}
